import { useMemo, useState } from "react";
import { AlertTriangle, Clock, MinusCircle } from "lucide-react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Skeleton } from "@/components/ui/Skeleton";
import { S, T, skipReasonLabel } from "@/lib/strings";
import { cn, formatSeconds } from "@/lib/utils";
import type { DispatchPreview } from "@/types/fleet";

/**
 * "You are about to do this to 87 phones."
 *
 * The count is the headline because it is the number that decides whether the
 * operator should be doing this at all. Under it, the two things a fleet action
 * does *quietly*: devices that will queue instead of running now, and devices
 * that will be skipped entirely. Both are surprises if they surface afterwards.
 *
 * Type-to-confirm is reserved for destructive actions. Asking someone to retype
 * a number for a routine dispatch trains them to type it without reading, which
 * costs the gesture exactly the meaning it was there to have.
 */
export function ConfirmImpactDialog({
  open,
  onOpenChange,
  title,
  description,
  preview,
  loading = false,
  destructive = false,
  confirmLabel,
  pending = false,
  onConfirm,
  affectedNames,
  extra,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  description?: string;
  preview: DispatchPreview | null;
  loading?: boolean;
  destructive?: boolean;
  confirmLabel: string;
  pending?: boolean;
  onConfirm: () => void;
  /** Labels of the devices in scope, for the affected list. */
  affectedNames?: string[];
  extra?: React.ReactNode;
}) {
  const [typed, setTyped] = useState("");

  // Clearing the confirmation field is part of closing the dialog, so it happens
  // in the close handler. Doing it in an effect keyed on `open` would also fire
  // on the way *in*, and would do it a render late.
  const handleOpenChange = (v: boolean) => {
    if (!v) setTyped("");
    onOpenChange(v);
  };

  const affected = preview?.affected ?? 0;
  const needsTyping = destructive && affected > 0;
  const typedOk = !needsTyping || typed.trim() === String(affected);

  const skipsByReason = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of preview?.skipped ?? []) m.set(s.reason, (m.get(s.reason) ?? 0) + 1);
    return [...m.entries()];
  }, [preview?.skipped]);

  const queued = (preview?.breakdown.stale ?? 0) + (preview?.breakdown.offline ?? 0);

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      description={description}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => handleOpenChange(false)} disabled={pending}>
            {S.cancel}
          </Button>
          <Button
            variant={destructive ? "danger" : "primary"}
            onClick={onConfirm}
            disabled={pending || loading || !typedOk || affected === 0}
          >
            {pending ? S.dispatchPending : confirmLabel}
          </Button>
        </>
      }
    >
      {loading || !preview ? (
        <div className="space-y-3">
          <Skeleton className="h-16 w-40" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* ---- the number ---- */}
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-5xl font-semibold tabular-nums tracking-tight text-[var(--color-fg)]">
              {affected}
            </span>
            <span className="text-sm text-[var(--color-fg-subtle)]">{S.impactAffected}</span>
          </div>

          {/* ---- what happens now vs later ---- */}
          <div className="space-y-1.5 rounded-lg border border-[var(--color-border-subtle)] bg-[rgba(255,255,255,0.02)] p-3">
            <Line tone="ok" text={`${preview.breakdown.online} 部${S.impactOnline}`} />
            {preview.breakdown.stale > 0 && (
              <Line icon={Clock} tone="warn" text={T.queuedStale(preview.breakdown.stale)} />
            )}
            {preview.breakdown.offline > 0 && (
              <Line icon={Clock} tone="warn" text={T.queuedOffline(preview.breakdown.offline)} />
            )}
            {skipsByReason.map(([reason, n]) => (
              <Line
                key={reason}
                icon={MinusCircle}
                tone="muted"
                text={`${n} 部${skipReasonLabel(reason)}`}
              />
            ))}
            {queued === 0 && skipsByReason.length === 0 && (
              <div className="text-[11px] text-[var(--color-fg-faint)]">
                全部裝置都在線，會即時執行。
              </div>
            )}
          </div>

          {/* ---- duration ---- */}
          <div className="flex items-center gap-2 text-xs text-[var(--color-fg-subtle)]">
            <Clock size={12} />
            {S.impactEstimated}
            <span className="font-mono text-[var(--color-fg)]">
              {formatSeconds(preview.estimated_seconds)}
            </span>
          </div>

          {extra}

          {/* ---- who ---- */}
          {affectedNames && affectedNames.length > 0 && (
            <details className="rounded-lg border border-[var(--color-border-subtle)]">
              <summary className="cursor-pointer px-3 py-2 text-xs text-[var(--color-fg-subtle)] hover:text-[var(--color-fg)]">
                受影響裝置（{affectedNames.length}）
              </summary>
              <div className="max-h-40 overflow-y-auto px-3 pb-2">
                <ul className="space-y-0.5 font-mono text-[11px] text-[var(--color-fg-muted)]">
                  {affectedNames.map((name, i) => (
                    <li key={`${name}-${i}`} className="truncate">
                      {name}
                    </li>
                  ))}
                </ul>
              </div>
            </details>
          )}

          {/* ---- type to confirm ---- */}
          {needsTyping && (
            <div className="rounded-lg border border-[rgba(239,68,68,0.25)] bg-[rgba(239,68,68,0.04)] p-3">
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-[#fca5a5]">
                <AlertTriangle size={12} />
                {S.impactTypeToConfirmHint}
              </div>
              <label className="block text-[11px] text-[var(--color-fg-subtle)]" htmlFor="impact-confirm">
                {S.impactTypeToConfirm}（{affected}）
              </label>
              <Input
                id="impact-confirm"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                inputMode="numeric"
                autoComplete="off"
                placeholder={String(affected)}
                className="mt-1 font-mono"
              />
              {typed !== "" && !typedOk && (
                <p className="mt-1 text-[11px] text-[#fca5a5]">{S.impactMismatch}</p>
              )}
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}

function Line({
  icon: Icon,
  tone,
  text,
}: {
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  tone: "ok" | "warn" | "muted";
  text: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-1.5 text-xs",
        tone === "ok" && "text-[#10b981]",
        tone === "warn" && "text-[#f59e0b]",
        tone === "muted" && "text-[var(--color-fg-subtle)]",
      )}
    >
      {Icon ? <Icon size={12} className="shrink-0" /> : <span className="size-1.5 rounded-full bg-current" />}
      {text}
    </div>
  );
}
